import { MessageOutlined } from "@ant-design/icons"
import { Alert, Button, Flex, Form, InputNumber } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { unwrapEnvelope } from "../../../../utils/api/unwrap-envelope.ts"
import { showResponseMessage } from "../../../../utils/handlers/show-response-message.ts"
import {
  type SmsRetentionSettings,
  getSmsRetentionSettings,
  updateSmsRetentionSettings
} from "../../api/sms-retention-settings.services.ts"
import { SettingsCard } from "../common/SettingsCard.tsx"

interface FormValues {
  sms_retention_days: number
}

const draftKey = (owner: string) => `settings.smsRetention.draft.${owner}`

const readDraft = (owner?: string): number | null => {
  if (!owner) return null
  try {
    const value = Number(window.sessionStorage.getItem(draftKey(owner)))
    return Number.isFinite(value) && value > 0 ? value : null
  } catch {
    return null
  }
}

const writeDraft = (
  owner: string | undefined,
  value: number | null | undefined
) => {
  if (!owner) return
  try {
    if (value) window.sessionStorage.setItem(draftKey(owner), String(value))
    else window.sessionStorage.removeItem(draftKey(owner))
  } catch {
    // Storage can be unavailable (private window); the draft is a convenience.
  }
}

const fieldErrorOf = (data: unknown): string[] | null => {
  const body = data as
    | {
        sms_retention_days?: string[] | string
        details?: { field_errors?: { sms_retention_days?: string[] | string } }
      }
    | null
    | undefined
  const reason =
    body?.details?.field_errors?.sms_retention_days ?? body?.sms_retention_days
  if (!reason) return null
  return Array.isArray(reason) ? reason : [reason]
}

export function SmsRetentionCard({
  readonly = false,
  draftOwner
}: {
  readonly?: boolean
  draftOwner?: string
}) {
  const { t } = useTranslation()
  const [settings, setSettings] = useState<SmsRetentionSettings | null>(null)
  const [saving, setSaving] = useState(false)
  const [loadState, setLoadState] = useState<"loading" | "loaded" | "error">(
    "loading"
  )
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [form] = Form.useForm<FormValues>()
  const [dirty, setDirty] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)
  const [draftRestored, setDraftRestored] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoadState("loading")

    void getSmsRetentionSettings().then((response) => {
      if (cancelled) return
      if (response?.ok && response.data) {
        setSettings(response.data)
        setLoadState("loaded")
      } else {
        setLoadState("error")
      }
    })

    return () => {
      cancelled = true
    }
  }, [loadAttempt])

  useEffect(() => {
    if (!settings || dirty) return
    const draft = readonly ? null : readDraft(draftOwner)
    if (draft !== null && draft !== settings.sms_retention_days) {
      form.setFieldsValue({ sms_retention_days: draft })
      setDirty(true)
      setDraftRestored(true)
      return
    }
    form.setFieldsValue({ sms_retention_days: settings.sms_retention_days })
  }, [form, settings, dirty, readonly, draftOwner])

  const discardDraft = () => {
    writeDraft(draftOwner, null)
    setDraftRestored(false)
    setDirty(false)
  }

  const handleFinish = async (values: FormValues) => {
    setSaving(true)
    setSaveFailed(false)
    try {
      const response = await updateSmsRetentionSettings(values)
      if (response?.ok && response.data) {
        setSettings(unwrapEnvelope<SmsRetentionSettings>(response.data))
        setDirty(false)
        setDraftRestored(false)
        writeDraft(draftOwner, null)
        showResponseMessage(
          response,
          t("settingsConfiguration.smsRetention.saved")
        )
        return
      }
      const reasons = fieldErrorOf(response?.data)
      if (reasons) {
        form.setFields([{ name: "sms_retention_days", errors: reasons }])
      } else {
        setSaveFailed(true)
      }
    } finally {
      setSaving(false)
    }
  }

  const minimum = settings?.minimum_days
  const maximum = settings?.maximum_days

  return (
    <SettingsCard
      iconWrapperClassName="bg-blue-100"
      icon={<MessageOutlined className="text-blue-600" />}
      title={t("settingsConfiguration.smsRetention.title")}
      subtitle={t("settingsConfiguration.smsRetention.subtitle")}
    >
      {loadState === "error" && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message={t("settingsConfiguration.smsRetention.loadError")}
          action={
            <Button size="small" onClick={() => setLoadAttempt((n) => n + 1)}>
              {t("settingsConfiguration.smsRetention.retry")}
            </Button>
          }
        />
      )}
      {saveFailed && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message={t("settingsConfiguration.smsRetention.saveFailed")}
        />
      )}
      {draftRestored && (
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message={t("settingsConfiguration.smsRetention.draftRestored")}
          action={
            <Button size="small" onClick={discardDraft}>
              {t("settingsConfiguration.smsRetention.discard")}
            </Button>
          }
        />
      )}
      {readonly && loadState === "loaded" && (
        <p className="mb-4 text-sm text-gray-500">
          {t("settingsConfiguration.smsRetention.viewOnly")}
        </p>
      )}
      <Form
        form={form}
        layout="vertical"
        className="with-default-error"
        disabled={readonly || saving || loadState !== "loaded"}
        onValuesChange={(changed: Partial<FormValues>) => {
          setDirty(true)
          setSaveFailed(false)
          writeDraft(draftOwner, changed.sms_retention_days)
        }}
        onFinish={handleFinish}
      >
        <Form.Item
          name="sms_retention_days"
          label={t("settingsConfiguration.smsRetention.label")}
          extra={
            settings
              ? t("settingsConfiguration.smsRetention.hint", {
                  minimum,
                  maximum,
                  default: settings.default_days
                })
              : undefined
          }
          rules={[
            {
              required: true,
              message: t("settingsConfiguration.smsRetention.required")
            },
            {
              type: "number",
              min: minimum,
              max: maximum,
              message: t("settingsConfiguration.smsRetention.outOfRange", {
                minimum,
                maximum
              })
            }
          ]}
        >
          <InputNumber
            precision={0}
            className="w-48"
            addonAfter={t("settingsConfiguration.smsRetention.days")}
            aria-label={t("settingsConfiguration.smsRetention.label")}
          />
        </Form.Item>
        <p className="mb-4 text-sm text-gray-500">
          {t("settingsConfiguration.smsRetention.keptRecords")}
        </p>

        <Flex justify="end">
          <Button
            type="primary"
            htmlType="submit"
            loading={saving}
            disabled={readonly || !dirty || loadState !== "loaded"}
          >
            {t("settingsConfiguration.smsRetention.save")}
          </Button>
        </Flex>
      </Form>
    </SettingsCard>
  )
}
